import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { mdiPlus, mdiTrashCan, mdiContentSave } from '@mdi/js';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import Grid, { GridItem } from '@/components/layout/Grid';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import AlertBox from '@/components/layout/AlertBox';

type Regra = {
    id?: number;
    catalogo_id?: number | null;
    escopo?: 'marcador' | 'cliente';
    marcador_id?: number | null;
    cliente_id?: number | null;
    marcador?: { id: number; nome: string };
    cliente?: { id: number; nome: string };
    percentual?: number;
    ativo?: boolean;
};

const VAZIO: Regra = { escopo: 'marcador', ativo: true };

const RegrasDesconto = ({ catalogos }: { catalogos: { id: number; nome: string }[] }) => {
    const [edicao, setEdicao] = React.useState<Regra | null>(null);

    const query = useQuery(
        ['catalogo/regra-desconto'],
        async () => (await axios.get('/api/catalogo/regra-desconto')).data.result ?? [],
    );

    const marcadores = useQuery(
        ['marcador/box'],
        async () => (await axios.get('/api/marcador/box')).data.result ?? [],
    );

    const salvar = useMutation(
        async (regra: Regra) =>
            regra.id
                ? axios.put(`/api/catalogo/regra-desconto/${regra.id}`, regra)
                : axios.post('/api/catalogo/regra-desconto', regra),
        {
            onSuccess: () => {
                toast('Regra salva', { type: 'success', position: 'top-center' });
                query.refetch();
                setEdicao(null);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao salvar regra', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const excluir = useMutation(
        async (id: number) => axios.delete(`/api/catalogo/regra-desconto/${id}`),
        {
            onSuccess: () => {
                toast('Regra excluída', { type: 'success', position: 'top-center' });
                query.refetch();
            },
        },
    );

    const cabecalhos: DataTableHeader[] = [
        { label: 'Escopo', width: '15%', render: (l: any) => (l.escopo === 'cliente' ? 'Cliente' : 'Marcador') },
        {
            label: 'Alvo',
            width: '30%',
            render: (l: any) => l.marcador?.nome ?? l.cliente?.nome ?? '—',
        },
        { label: 'Desconto', width: '15%', render: (l: any) => `${Number(l.percentual)}%` },
        { label: 'Ativo', width: '10%', render: (l: any) => (l.ativo ? 'Sim' : 'Não') },
        {
            label: 'Ações',
            width: '120px',
            render: (l: any) => (
                <HBox gap={8}>
                    <Botao variant="none-primary" title="Editar" icon={mdiContentSave} onClick={() => setEdicao(l)} />
                    <Botao variant="none-danger" title="Excluir" icon={mdiTrashCan} onClick={() => excluir.mutate(l.id)} />
                </HBox>
            ),
        },
    ];

    return (
        <VBox gap={16}>
            <AlertBox variant="info">
                <p>
                    Desconto fixo do cliente ou de um marcador que ele carrega (ex.:{' '}
                    <em>revenda = 15%</em>). Quando mais de uma regra se aplica, vale a de maior
                    percentual — elas não somam.
                </p>
            </AlertBox>

            <HBox>
                <div style={{ flex: 1 }} />
                <Botao variant="primary" icon={mdiPlus} title="Nova regra" onClick={() => setEdicao({ ...VAZIO })}>
                    Nova regra
                </Botao>
            </HBox>

            {edicao && (
                <Grid>
                    <GridItem sm={12} md={3}>
                        <CampoSeletor
                            instanceId="regra-escopo"
                            label="Escopo"
                            options={[
                                { id: 'marcador', label: 'Marcador' },
                                { id: 'cliente', label: 'Cliente específico' },
                            ]}
                            value={edicao.escopo ?? 'marcador'}
                            // Trocar o escopo zera o alvo anterior: uma regra "cliente" com
                            // marcador_id preenchido nunca casaria com ninguém.
                            onChange={(v: string | null) =>
                                setEdicao({
                                    ...edicao,
                                    escopo: (v ?? 'marcador') as Regra['escopo'],
                                    marcador_id: null,
                                    cliente_id: null,
                                })
                            }
                        />
                    </GridItem>

                    <GridItem sm={12} md={3}>
                        {edicao.escopo === 'cliente' ? (
                            <CampoNumero
                                instanceId="regra-cliente"
                                label="ID do cliente"
                                value={edicao.cliente_id != null ? String(edicao.cliente_id) : ''}
                                decimais={0}
                                somentePositivos
                                onChange={(v: string) =>
                                    setEdicao({ ...edicao, cliente_id: v ? Number(v) : null })
                                }
                            />
                        ) : (
                            <CampoSeletor
                                instanceId="regra-marcador"
                                label="Marcador"
                                options={(marcadores.data ?? []).map((m: any) => ({
                                    id: String(m.id),
                                    label: m.nome,
                                }))}
                                value={edicao.marcador_id ? String(edicao.marcador_id) : null}
                                onChange={(v: string | null) =>
                                    setEdicao({ ...edicao, marcador_id: v ? Number(v) : null })
                                }
                                loadingState={marcadores.isLoading ? 'loading' : 'success'}
                            />
                        )}
                    </GridItem>

                    <GridItem sm={12} md={2}>
                        <CampoNumero
                            instanceId="regra-percentual"
                            label="Desconto (%)"
                            value={edicao.percentual != null ? String(edicao.percentual) : ''}
                            decimais={2}
                            somentePositivos
                            onChange={(v: string) =>
                                setEdicao({ ...edicao, percentual: v ? Number(v) : undefined })
                            }
                        />
                    </GridItem>

                    <GridItem sm={12} md={2}>
                        <CampoSeletor
                            instanceId="regra-catalogo"
                            label="Catálogo (vazio = todos)"
                            nullable
                            options={catalogos.map((c) => ({ id: String(c.id), label: c.nome }))}
                            value={edicao.catalogo_id ? String(edicao.catalogo_id) : null}
                            onChange={(v: string | null) =>
                                setEdicao({ ...edicao, catalogo_id: v ? Number(v) : null })
                            }
                        />
                    </GridItem>

                    <GridItem sm={12} md={2}>
                        <VBox gap={8}>
                            <CampoCheckbox
                                instanceId="regra-ativo"
                                label="Ativa"
                                value={edicao.ativo !== false}
                                onChange={(v: boolean) => setEdicao({ ...edicao, ativo: v })}
                            />
                            <HBox gap={8}>
                                <Botao variant="primary" onClick={() => salvar.mutate(edicao)} disabled={salvar.isLoading}>
                                    Salvar
                                </Botao>
                                <Botao variant="none-primary" onClick={() => setEdicao(null)}>
                                    Cancelar
                                </Botao>
                            </HBox>
                        </VBox>
                    </GridItem>
                </Grid>
            )}

            <DataTable cabecalhos={cabecalhos} linhas={query.data ?? []} />
        </VBox>
    );
};

export default RegrasDesconto;
